import clsx from 'clsx'
import React, { useEffect, useMemo } from 'react'

import OptionTooltip from '@deltek/specification-client/core/Feedbacks/OptionTooltip'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { manufacturerColor } from '../../colorVars'

import {
    INFO_ICON_URL,
    MANUFACTURER_OPTION,
    MASTERSPEC_OPTION,
    PROPERTY_VALUE,
} from './constants'

const useStyles = makeStyles((theme: Theme) => ({
    iconBase: {
        display: 'inline',
        width: pxToRem(20),
        height: pxToRem(20),
        marginLeft: theme.spacing(1.25),
        borderRadius: 3,
        cursor: 'pointer',
    },
    icon: {
        display: 'inline',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: theme.spacing(1),
        width: pxToRem(20),
        height: pxToRem(20),
    },
    small: {
        maxWidth: pxToRem(400),
        width: pxToRem(363),
        height: pxToRem(146),
    },
    medium: {
        maxWidth: pxToRem(400),
        width: pxToRem(378),
        height: pxToRem(168),
    },
    tooltip: {
        '&.MuiTooltip-tooltipPlacementBottom': {
            top: 3,
            '& .MuiTooltip-arrow': {
                '&::after': {
                    left: 24,
                },
            },
        },
    },
    buttonOption: {
        fontSize: pxToRem(14),
        fontWeight: 500,
        borderRadius: pxToRem(20),
        lineHeight: '1.7',
        border: `1px solid #F38936`,
    },
    buttonMSOption: (props: PremiumInfoIconTooltipProps) => ({
        color: props.mode === 'workspace' ? '#40464F' : '#F38936',
        backgroundColor:
            props.mode === 'workspace' ? theme.palette.common.white : '#FEF9F5',
        borderColor: props.mode === 'workspace' ? '#40464F' : '#F38936',
    }),
    buttonMSOptionSelected: {
        backgroundColor: '#F38936',
        color: theme.palette.common.white,
    },
    buttonManufacturerOption: (props: PremiumInfoIconTooltipProps) => ({
        borderColor: manufacturerColor,
        color: manufacturerColor,
        backgroundColor:
            props.mode === 'workspaceAdded'
                ? '#F2F7F6'
                : theme.palette.common.white,
    }),
    buttonManufacturerOptionSelected: {
        borderColor: manufacturerColor,
        color: theme.palette.common.white,
        backgroundColor: manufacturerColor,
    },
    optionText: {
        paddingLeft: theme.spacing(2),
    },
    dialogDetailContainer: {
        height: pxToRem(19),
        margin: theme.spacing(1),
        alignItems: 'center',
    },
    buttonOptionDialog: {
        textAlign: 'center',
    },
    singleItemContainer: {
        top: theme.spacing(0.875),
        maxWidth: pxToRem(400),
        width: pxToRem(309),
        height: pxToRem(72),
    },
}))

interface PremiumInfoIconTooltipProps {
    open: boolean
    size?: 'small' | 'medium'
    mode?: 'productSelection' | 'workspace' | 'workspaceAdded'
    iconClassName?: string
    onClick?: () => void
    onClickOutside?: () => void
}

export const PremiumInfoIconTooltip: React.FC<PremiumInfoIconTooltipProps> = (
    props
) => {
    const {
        open = false,
        size = 'small',
        mode = 'workspaceAdded',
        iconClassName,
        onClick,
        onClickOutside,
    } = props
    const classes = useStyles(props)

    useEffect(() => {
        if (onClickOutside)
            document.addEventListener('click', onClickOutside, true)
        return () => {
            if (onClickOutside)
                document.removeEventListener('click', onClickOutside, true)
        }
    }, [onClickOutside])

    const customTooltip = useMemo(() => {
        const col1Size = mode === 'workspace' ? 5 : 4
        const col2Size = mode === 'workspace' ? 7 : 8
        const postfix = mode === 'productSelection' ? 'compliant' : 'selected'
        return (
            <>
                <GridContainer
                    wrap="nowrap"
                    className={classes.dialogDetailContainer}
                >
                    <GridItem
                        xs={col1Size}
                        className={clsx([
                            classes.buttonOption,
                            classes.buttonMSOption,
                            classes.buttonOptionDialog,
                        ])}
                    >
                        {PROPERTY_VALUE}
                    </GridItem>
                    <GridItem xs={col2Size} className={classes.optionText}>
                        {MASTERSPEC_OPTION}
                    </GridItem>
                </GridContainer>
                {mode !== 'workspace' && (
                    <>
                        <GridContainer
                            wrap="nowrap"
                            className={classes.dialogDetailContainer}
                        >
                            <GridItem
                                xs={col1Size}
                                className={clsx([
                                    classes.buttonOption,
                                    classes.buttonMSOptionSelected,
                                    classes.buttonOptionDialog,
                                ])}
                            >
                                {PROPERTY_VALUE}
                            </GridItem>
                            <GridItem
                                xs={col2Size}
                                className={classes.optionText}
                            >
                                {MASTERSPEC_OPTION} {postfix}
                            </GridItem>
                        </GridContainer>
                        {mode === 'workspaceAdded' && (
                            <GridContainer
                                wrap="nowrap"
                                className={classes.dialogDetailContainer}
                                data-testid="manufacturerOptionProp"
                            >
                                <GridItem
                                    xs={col1Size}
                                    className={clsx([
                                        classes.buttonOption,
                                        classes.buttonManufacturerOption,
                                        classes.buttonOptionDialog,
                                    ])}
                                >
                                    {PROPERTY_VALUE}
                                </GridItem>
                                <GridItem
                                    xs={col2Size}
                                    className={classes.optionText}
                                >
                                    {MANUFACTURER_OPTION}
                                </GridItem>
                            </GridContainer>
                        )}
                        <GridContainer
                            wrap="nowrap"
                            className={classes.dialogDetailContainer}
                        >
                            <GridItem
                                xs={col1Size}
                                className={clsx([
                                    classes.buttonOption,
                                    classes.buttonManufacturerOptionSelected,
                                    classes.buttonOptionDialog,
                                ])}
                            >
                                {PROPERTY_VALUE}
                            </GridItem>
                            <GridItem
                                xs={col2Size}
                                className={classes.optionText}
                            >
                                {MANUFACTURER_OPTION} {postfix}
                            </GridItem>
                        </GridContainer>
                    </>
                )}
            </>
        )
    }, [classes, mode])

    const otherTooltipClass = useMemo(() => {
        if (mode === 'workspace') return classes.singleItemContainer
        return (size && classes[size]) || ''
    }, [size, classes, mode])

    return (
        <OptionTooltip
            open={open}
            title={customTooltip}
            padding={14}
            tooltipClass={`${classes.tooltip} ${otherTooltipClass}`}
        >
            <div
                className={clsx(iconClassName, classes.iconBase)}
                data-testid={`propertyInfo`}
                onClick={onClick}
            >
                <img
                    data-testid="imgPropertyInfo"
                    className={classes.icon}
                    src={INFO_ICON_URL}
                    alt="info-icon"
                />
            </div>
        </OptionTooltip>
    )
}

export default PremiumInfoIconTooltip
